// Canvas API를 사용하여 모자이크와 blur 효과를 생성하는 유틸리티

export interface EffectOptions {
  width: number;
  height: number;
  cellSize?: number; // 모자이크 셀 크기
  blurRadius?: number; // blur 반지름
  color?: string; // 기본 색상
}

/**
 * 모자이크 패턴을 생성합니다
 */
export const generateMosaicPattern = (options: EffectOptions): string => {
  const { width, height, cellSize = 10, color = '#3B82F6' } = options;
  
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  
  if (!ctx) {
    return '';
  }

  // 배경색 설정
  ctx.fillStyle = color;
  ctx.fillRect(0, 0, width, height);

  // 모자이크 패턴 생성 - 더 정교한 효과
  for (let x = 0; x < width; x += cellSize) {
    for (let y = 0; y < height; y += cellSize) {
      // 색상 파싱
      const baseR = parseInt(color.slice(1, 3), 16);
      const baseG = parseInt(color.slice(3, 5), 16);
      const baseB = parseInt(color.slice(5, 7), 16);
      
      // 랜덤한 색상 변화
      const variation = 30;
      const r = Math.max(0, Math.min(255, baseR + (Math.random() - 0.5) * variation));
      const g = Math.max(0, Math.min(255, baseG + (Math.random() - 0.5) * variation));
      const b = Math.max(0, Math.min(255, baseB + (Math.random() - 0.5) * variation));
      
      ctx.fillStyle = `rgb(${r}, ${g}, ${b})`;
      ctx.fillRect(x, y, cellSize - 1, cellSize - 1);
      
      // 하이라이트 효과
      if (Math.random() > 0.7) {
        ctx.fillStyle = 'rgba(255, 255, 255, 0.2)';
        ctx.fillRect(x, y, cellSize / 3, cellSize / 3);
      }
    }
  }

  return canvas.toDataURL();
};

/**
 * 디지털 모자이크 패턴을 생성합니다
 */
export const generateDigitalMosaicPattern = (options: EffectOptions): string => {
  const { width, height, cellSize = 4, color = '#3B82F6' } = options;
  
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  
  if (!ctx) {
    return '';
  }

  // 배경
  ctx.fillStyle = '#000000';
  ctx.fillRect(0, 0, width, height);

  // 디지털 픽셀 효과
  for (let x = 0; x < width; x += cellSize) {
    for (let y = 0; y < height; y += cellSize) {
      if (Math.random() > 0.3) {
        // 색상 채널 분리 효과
        const channel = Math.floor(Math.random() * 3);
        if (channel === 0) {
          ctx.fillStyle = `rgba(255, 0, 0, ${Math.random() * 0.8 + 0.2})`;
        } else if (channel === 1) {
          ctx.fillStyle = `rgba(0, 255, 0, ${Math.random() * 0.8 + 0.2})`;
        } else {
          ctx.fillStyle = `rgba(0, 0, 255, ${Math.random() * 0.8 + 0.2})`;
        }
        ctx.fillRect(x, y, cellSize - 1, cellSize - 1);
      }
    }
  }

  // 오버레이로 주 색상 적용
  ctx.fillStyle = color + '40'; // 25% 투명도
  ctx.fillRect(0, 0, width, height);

  return canvas.toDataURL();
};

/**
 * 글래스모픽 효과를 생성합니다
 */
export const generateGlassmorphismPattern = (options: EffectOptions): string => {
  const { width, height, color = '#3B82F6' } = options;
  
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  
  if (!ctx) {
    return '';
  }

  // 글래스모픽 베이스
  const gradient = ctx.createLinearGradient(0, 0, width, height);
  gradient.addColorStop(0, 'rgba(255, 255, 255, 0.3)');
  gradient.addColorStop(0.5, 'rgba(255, 255, 255, 0.1)');
  gradient.addColorStop(1, 'rgba(255, 255, 255, 0.2)');
  
  ctx.fillStyle = gradient;
  ctx.fillRect(0, 0, width, height);

  // 색상 오버레이
  ctx.fillStyle = color + '20'; // 12.5% 투명도
  ctx.fillRect(0, 0, width, height);

  // 노이즈 효과
  const imageData = ctx.getImageData(0, 0, width, height);
  const data = imageData.data;
  
  for (let i = 0; i < data.length; i += 4) {
    const noise = (Math.random() - 0.5) * 20;
    data[i] = Math.min(255, Math.max(0, data[i] + noise));
    data[i + 1] = Math.min(255, Math.max(0, data[i + 1] + noise));
    data[i + 2] = Math.min(255, Math.max(0, data[i + 2] + noise));
  }
  
  ctx.putImageData(imageData, 0, 0);

  // 광택 효과
  const shine = ctx.createLinearGradient(0, 0, width * 0.5, height * 0.5);
  shine.addColorStop(0, 'rgba(255, 255, 255, 0.4)');
  shine.addColorStop(0.5, 'rgba(255, 255, 255, 0.1)');
  shine.addColorStop(1, 'rgba(255, 255, 255, 0)');
  
  ctx.fillStyle = shine;
  ctx.fillRect(0, 0, width * 0.7, height * 0.7);

  return canvas.toDataURL();
};

/**
 * blur 효과가 적용된 패턴을 생성합니다
 */
export const generateBlurPattern = (options: EffectOptions): string => {
  const { width, height, blurRadius = 5, color = '#3B82F6' } = options;
  
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  
  if (!ctx) {
    return '';
  }

  // blur 필터 적용
  ctx.filter = `blur(${blurRadius}px)`;

  // 그라데이션 생성
  const gradient = ctx.createRadialGradient(
    width / 2, height / 2, 0,
    width / 2, height / 2, Math.max(width, height) / 2
  );
  
  gradient.addColorStop(0, color);
  gradient.addColorStop(0.5, `${color}80`); // 50% 투명도
  gradient.addColorStop(1, `${color}20`); // 80% 투명도

  ctx.fillStyle = gradient;
  ctx.fillRect(0, 0, width, height);

  // 추가적인 blur 점들
  ctx.filter = `blur(${blurRadius * 2}px)`;
  for (let i = 0; i < 10; i++) {
    const x = Math.random() * width;
    const y = Math.random() * height;
    const radius = Math.random() * 20 + 10;
    
    ctx.fillStyle = `${color}40`;
    ctx.beginPath();
    ctx.arc(x, y, radius, 0, Math.PI * 2);
    ctx.fill();
  }

  return canvas.toDataURL();
};

/**
 * 색상 기반으로 모자이크 텍스처를 생성합니다
 */
export const generateColorMosaic = (color: string, width: number, height: number, cellSize = 8): string => {
  return generateMosaicPattern({ width, height, cellSize, color });
};

/**
 * 색상 기반으로 blur 텍스처를 생성합니다
 */
export const generateColorBlur = (color: string, width: number, height: number, blurRadius = 3): string => {
  return generateBlurPattern({ width, height, blurRadius, color });
};

/**
 * 이미지에 모자이크 효과를 적용합니다
 */
export const applyMosaicToImage = (imageUrl: string, cellSize = 10): Promise<string> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext('2d');
      
      if (!ctx) {
        reject(new Error('Canvas context not available'));
        return;
      }

      // 원본 이미지 그리기
      ctx.drawImage(img, 0, 0);
      
      // 이미지 데이터 가져오기
      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const data = imageData.data;

      // 모자이크 효과 적용
      for (let y = 0; y < canvas.height; y += cellSize) {
        for (let x = 0; x < canvas.width; x += cellSize) {
          // 셀 영역의 평균 색상 계산
          let r = 0, g = 0, b = 0, a = 0;
          let pixelCount = 0;

          for (let dy = 0; dy < cellSize && y + dy < canvas.height; dy++) {
            for (let dx = 0; dx < cellSize && x + dx < canvas.width; dx++) {
              const index = ((y + dy) * canvas.width + (x + dx)) * 4;
              r += data[index];
              g += data[index + 1];
              b += data[index + 2];
              a += data[index + 3];
              pixelCount++;
            }
          }

          // 평균 색상
          r = Math.floor(r / pixelCount);
          g = Math.floor(g / pixelCount);
          b = Math.floor(b / pixelCount);
          a = Math.floor(a / pixelCount);

          // 셀 전체를 평균 색상으로 채우기
          ctx.fillStyle = `rgba(${r}, ${g}, ${b}, ${a / 255})`;
          ctx.fillRect(x, y, cellSize, cellSize);
        }
      }

      resolve(canvas.toDataURL());
    };

    img.onerror = () => reject(new Error('Failed to load image'));
    img.src = imageUrl;
  });
};

/**
 * 이미지에 blur 효과를 적용합니다
 */
export const applyBlurToImage = (imageUrl: string, blurRadius = 5): Promise<string> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext('2d');
      
      if (!ctx) {
        reject(new Error('Canvas context not available'));
        return;
      }

      // blur 필터 적용하여 이미지 그리기
      ctx.filter = `blur(${blurRadius}px)`;
      ctx.drawImage(img, 0, 0);

      resolve(canvas.toDataURL());
    };

    img.onerror = () => reject(new Error('Failed to load image'));
    img.src = imageUrl;
  });
};

/**
 * 어안 왜곡 효과를 적용합니다
 * fisheyegl 패키지 사용 시도, 실패 시 Canvas API fallback
 */
export const applyFisheyeEffect = (imageUrl: string, strength = 0.5): Promise<string> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    
    img.onload = async () => {
      try {
        // fisheyegl 패키지 동적 import 시도
        const Fisheyegl = (await import('fisheyegl')).default;
        
        const canvas = document.createElement('canvas');
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext('2d');
        
        if (!ctx) {
          throw new Error('Canvas context not available');
        }

        // 원본 이미지 그리기
        ctx.drawImage(img, 0, 0);
        
        // fisheyegl 적용
        const fisheye = new Fisheyegl({
          canvas: canvas,
          lens: {
            a: strength * 0.5 + 0.5, // 0.5-1.0 범위로 조정
            b: strength * 0.5 + 0.5,
            Fx: 0.0,
            Fy: 0.0,
            scale: 1.5
          },
          fov: {
            x: Math.PI * (0.5 + strength * 0.5),
            y: Math.PI * (0.5 + strength * 0.5)
          }
        });
        
        fisheye.setImage(imageUrl);
        
        // fisheyegl은 비동기로 렌더링되므로 약간의 지연 필요
        setTimeout(() => {
          resolve(canvas.toDataURL());
        }, 100);
        
      } catch {  // Removed unused error variable
        // console.error('Fisheye effect failed, falling back to Canvas API:', error);
        // fisheyegl 사용 실패 시 Canvas API fallback
        const canvas = document.createElement('canvas');
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext('2d');
        
        if (!ctx) {
          reject(new Error('Canvas context not available'));
          return;
        }

        // 원본 이미지 그리기
        ctx.drawImage(img, 0, 0);
        
        // 이미지 데이터 가져오기
        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const data = imageData.data;
        const tempCanvas = document.createElement('canvas');
        tempCanvas.width = canvas.width;
        tempCanvas.height = canvas.height;
        const tempCtx = tempCanvas.getContext('2d');
        
        if (!tempCtx) {
          reject(new Error('Temp canvas context not available'));
          return;
        }
        
        tempCtx.drawImage(img, 0, 0);
        const originalData = tempCtx.getImageData(0, 0, canvas.width, canvas.height).data;

        const centerX = canvas.width / 2;
        const centerY = canvas.height / 2;
        const radius = Math.min(centerX, centerY);

        // 어안 왜곡 적용
        for (let y = 0; y < canvas.height; y++) {
          for (let x = 0; x < canvas.width; x++) {
            const dx = x - centerX;
            const dy = y - centerY;
            const distance = Math.sqrt(dx * dx + dy * dy);
            
            if (distance < radius) {
              const percent = distance / radius;
              const distortion = 1 - percent * percent * strength;
              
              const sourceX = Math.floor(centerX + dx * distortion);
              const sourceY = Math.floor(centerY + dy * distortion);
              
              if (sourceX >= 0 && sourceX < canvas.width && sourceY >= 0 && sourceY < canvas.height) {
                const targetIndex = (y * canvas.width + x) * 4;
                const sourceIndex = (sourceY * canvas.width + sourceX) * 4;
                
                data[targetIndex] = originalData[sourceIndex];
                data[targetIndex + 1] = originalData[sourceIndex + 1];
                data[targetIndex + 2] = originalData[sourceIndex + 2];
                data[targetIndex + 3] = originalData[sourceIndex + 3];
              }
            }
          }
        }

        ctx.putImageData(imageData, 0, 0);
        resolve(canvas.toDataURL());
      }
    };

    img.onerror = () => reject(new Error('Failed to load image'));
    img.src = imageUrl;
  });
};

/**
 * 광각 왜곡 효과를 적용합니다
 */
export const applyWideAngleEffect = (imageUrl: string, strength = 0.3): Promise<string> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext('2d');
      
      if (!ctx) {
        reject(new Error('Canvas context not available'));
        return;
      }

      const centerX = canvas.width / 2;
      const centerY = canvas.height / 2;

      // 변환 매트릭스 적용
      ctx.save();
      ctx.translate(centerX, centerY);
      ctx.scale(1 + strength * 0.2, 1 + strength * 0.1);
      ctx.translate(-centerX, -centerY);
      
      // 원본 이미지 그리기
      ctx.drawImage(img, 0, 0);
      
      ctx.restore();

      // 가장자리 비네팅 효과
      const gradient = ctx.createRadialGradient(
        centerX, centerY, 0,
        centerX, centerY, Math.max(canvas.width, canvas.height) * 0.7
      );
      gradient.addColorStop(0, 'rgba(0,0,0,0)');
      gradient.addColorStop(0.7, 'rgba(0,0,0,0)');
      gradient.addColorStop(1, `rgba(0,0,0,${strength * 0.5})`);
      
      ctx.fillStyle = gradient;
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      resolve(canvas.toDataURL());
    };

    img.onerror = () => reject(new Error('Failed to load image'));
    img.src = imageUrl;
  });
};